import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Tags, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { PageHeader, Empty, Field } from "@/components/UI";
import { formatCurrency } from "@/lib/format";
import { PriceMemoryCard } from "@/components/dms/PriceMemoryCard";

export default function PriceList() {
  const toast = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", unit_of_measure: "Pcs", unit_price: "", category: "" });
  const [editing, setEditing] = useState(null); // {id, name, unit_of_measure, unit_price, category}
  const [supplierMap, setSupplierMap] = useState({});

  useEffect(() => {
    api.listSuppliers().then((sups) => {
      const map = {};
      sups.forEach((s) => (s.supplied_items || []).forEach((it) => {
        (map[it.product_id] ||= []).push(s.name);
      }));
      setSupplierMap(map);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.listProducts()
      .then(setProducts)
      .catch(() => toast("Failed to load price list", "error"))
      .finally(() => setLoading(false));
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.name.trim()) { toast("Enter a product name", "error"); return; }
    try {
      await api.createProduct(form);
      toast("Product added", "success");
      setForm({ name: "", unit_of_measure: "Pcs", unit_price: "", category: "" });
      load();
    } catch (e) {
      toast(e?.response?.data?.detail || "Add failed", "error");
    }
  };

  const saveEdit = async () => {
    try {
      await api.updateProduct(editing.id, editing);
      toast("Saved", "success");
      setEditing(null);
      load();
    } catch { toast("Save failed", "error"); }
  };

  const toggleActive = async (p) => {
    try { await api.updateProduct(p.id, { active: !p.active }); load(); }
    catch { toast("Update failed", "error"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}" from the price list?`)) return;
    try { await api.deleteProduct(p.id); toast("Deleted", "success"); load(); }
    catch { toast("Delete failed", "error"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="page-price-list">
      <PageHeader
        icon={Tags}
        title="Price List"
        subtitle="Official product catalog — AI-drafted quotations use these prices first"
      />

      <div className="card card-padded mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <div className="sm:col-span-2">
            <Field label="Product / Service name *">
              <input data-testid="product-name" className="input" placeholder="e.g. 1080P CCTV Camera (Outdoor)"
                value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Field>
          </div>
          <Field label="Unit">
            <input data-testid="product-uom" className="input" value={form.unit_of_measure}
              onChange={(e) => setForm({ ...form, unit_of_measure: e.target.value })} />
          </Field>
          <Field label="Unit price (UGX)">
            <input data-testid="product-price" type="number" min="0" className="input" placeholder="250000"
              value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} />
          </Field>
          <button data-testid="product-add" onClick={add} className="btn btn-primary justify-center">
            <Plus size={14} /> Add product
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <p className="text-sm muted p-5">Loading…</p>
        ) : products.length === 0 ? (
          <Empty icon={Tags} title="No products yet"
            description="Add your products and services with official prices — AI quote drafts will use them automatically." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider muted border-b border-slate-100">
                  <th className="px-5 py-3">Product / Service</th>
                  <th className="px-3 py-3">Unit</th>
                  <th className="px-3 py-3 text-right">Unit price</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3 text-right pr-5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`product-row-${p.id}`}>
                    {editing?.id === p.id ? (
                      <>
                        <td className="px-5 py-2">
                          <input data-testid="edit-name" className="input" value={editing.name}
                            onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                        </td>
                        <td className="px-3 py-2">
                          <input className="input w-20" value={editing.unit_of_measure}
                            onChange={(e) => setEditing({ ...editing, unit_of_measure: e.target.value })} />
                        </td>
                        <td className="px-3 py-2">
                          <input data-testid="edit-price" type="number" className="input w-32 text-right" value={editing.unit_price}
                            onChange={(e) => setEditing({ ...editing, unit_price: e.target.value })} />
                        </td>
                        <td className="px-3 py-2" />
                        <td className="px-3 py-2 text-right pr-5 whitespace-nowrap">
                          <button data-testid="edit-save-product" onClick={saveEdit} className="text-[#0e6939] p-1.5" title="Save"><Check size={15} /></button>
                          <button onClick={() => setEditing(null)} className="text-slate-400 p-1.5" title="Cancel"><X size={15} /></button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className={`px-5 py-2.5 font-medium ${p.active ? "" : "opacity-40 line-through"}`}>
                          {p.name}
                          {supplierMap[p.id]?.length > 0 && (
                            <span className="ml-2 text-[10px] font-semibold bg-sky-50 text-sky-700 border border-sky-100 rounded-full px-1.5 py-0.5"
                              data-testid={`product-suppliers-${p.id}`}
                              title={`Suppliers: ${supplierMap[p.id].join(", ")}`}>
                              via {supplierMap[p.id][0]}{supplierMap[p.id].length > 1 ? ` +${supplierMap[p.id].length - 1}` : ""}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 muted">{p.unit_of_measure}</td>
                        <td className="px-3 py-2.5 text-right font-semibold" style={{ color: "var(--green-700)" }}>
                          {formatCurrency(p.unit_price, p.currency)}
                        </td>
                        <td className="px-3 py-2.5">
                          <button
                            data-testid={`product-active-${p.id}`}
                            onClick={() => toggleActive(p)}
                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${p.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}
                            title="Click to toggle"
                          >
                            {p.active ? "active" : "hidden"}
                          </button>
                        </td>
                        <td className="px-3 py-2.5 text-right pr-5 whitespace-nowrap">
                          <button
                            data-testid={`product-edit-${p.id}`}
                            onClick={() => setEditing({ id: p.id, name: p.name, unit_of_measure: p.unit_of_measure, unit_price: p.unit_price, category: p.category })}
                            className="text-slate-500 hover:text-[#0e6939] p-1.5 text-xs font-semibold"
                          >
                            Edit
                          </button>
                          <button data-testid={`product-delete-${p.id}`} onClick={() => remove(p)}
                            className="text-slate-400 hover:text-red-600 p-1.5"><Trash2 size={14} /></button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-5">
        <PriceMemoryCard />
      </div>
    </div>
  );
}
